import { useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { translations } from '../../configs/translations'
import RaceService from '../../services/RaceService'
import { useStore } from '../../store/store'
import { getTimeFormated } from '../../utils/getTimeFormated'
import { ConsumablesShowMenu } from './consumablesShowMenu/ConsumablesShowMenu'
import { Disqualification } from './disqualification/Disqualification'
import { GameHudHints } from './gamehud-hints/GameHudHints'
import './GameHud.css'
import { Consumables, RaceData } from './GameHud.types'
import { ShortTermAchievement } from './shortTermAchievement/ShortTermAchievement'

type RaceType = "race" | "training"

export const GameHud = () => {
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [progress, setProgress] = useState(0)

    const [searchParams] = useSearchParams()
    const typeId = Number(searchParams.get("typeId")) ?? 0
    const type: RaceType = typeId === 0 ? "race" : "training"

    const [totalTimeMinutes, setTotalTimeMinutes] = useState(0)
    const [totalTimeSeconds, setTotalTimeSeconds] = useState(0)

    const [lapStartedMillis, setLapStartedMillis] = useState(0)
    const [lapTimeMinutes, setLapTimeMinutes] = useState(0)
    const [lapTimeSeconds, setLapTimeSeconds] = useState(0)
    const [lastLapTimeMinutes, setLastLapTimeMinutes] = useState(0)
    const [lastLapTimeSeconds, setLastLapTimeSeconds] = useState(0)

    const [bestLapTimeMinutes, setBestLapTimeMinutes] = useState(0)
    const [bestLapTimeSeconds, setBestLapTimeSeconds] = useState(0)

    const [raceStartedMillis, setRaceStartedMillis] = useState(0)

    const [position, setPosition] = useState(0)
    const [totalPlayer, setTotalPlayer] = useState(0)

    const [lap, setLap] = useState(0)
    const [totalLaps, setTotalLaps] = useState(0)

    const [totalTimeInterval, setTotalTimeInterval] = useState<NodeJS.Timeout | null>(null)
    const [lapTimeInterval, setLapTimeInterval] = useState<NodeJS.Timeout | null>(null)

    const [shortTermAchivementTitle, setShortTermAchivementTitle] = useState('')
    const [shortTermAchivementX, setShortTermAchivementX] = useState(1)
    const [animationKey, setAnimationKey] = useState(0)

    const [isRacing, setIsRacing] = useState(false)

    const [fuel, setFuel] = useState(0)
    const [nitro, setNitro] = useState(0)
    const [isDisqualified, setIsDisqualified] = useState(false)

    const applyResponse = (raceData: RaceData) => {
        setRaceStartedMillis(raceData.raceStartedMillis)
        setProgress(raceData.progress)
        setLapStartedMillis(raceData.lapStartedMillis)
        setLastLapTimeMinutes(raceData.lastLap.minutes)
        setLastLapTimeSeconds(raceData.lastLap.seconds)
        setBestLapTimeMinutes(raceData.bestLap.minutes)
        setBestLapTimeSeconds(raceData.bestLap.seconds)
        setPosition(raceData.place)
        setTotalPlayer(raceData.totalPlayers)
        setIsRacing(raceData.racing)
        setLap(raceData.lap)
        setTotalLaps(raceData.totalLaps)
        if (raceData.shortTermAchievement) {
            setShortTermAchivementTitle(raceData.shortTermAchievement.name)
            setShortTermAchivementX(raceData.shortTermAchievement.x)
            setAnimationKey(prev => prev + 1)
            setTimeout(() => {
                setShortTermAchivementTitle('')
                setShortTermAchivementX(1)
            }, 1500)
        }
    }

    const applyConsumables = (consumables: Consumables) => {
        setFuel(consumables.fuel)
        setNitro(consumables.nitro)
        setIsDisqualified(consumables.fuel <= 0)
    }

    const isConsumableDataEqual = (data: Consumables) => {
        return data.fuel === fuel && data.nitro === nitro
    }

    useEffect(() => {
        const token = localStorage.getItem('accessToken')
        const eventSource = RaceService.getRaceDataSSE(token as string || '')

        eventSource.addEventListener("INIT", (e) => {
            console.log("SSE connected:", e.data)
        })

        eventSource.addEventListener("RACE_UPDATE", (e) => {
            try {
                const response: RaceData = JSON.parse(e.data)

                applyResponse(response)
            } catch (err) {
                console.error(err)
            }
        })

        eventSource.addEventListener("RACE_CONSUMABLES_UPDATE", (e) => {
            try {
                const response: Consumables = JSON.parse(e.data)

                if (!isConsumableDataEqual(response)) {
                    applyConsumables(response)
                }
            } catch (err) {
                console.error(err)
            }
        })

        eventSource.onerror = (err) => {
            eventSource.close()
        }

        return () => {
            eventSource.close()
        }
    }, [])

    useEffect(() => {
        if (!isRacing) return

        if (raceStartedMillis) {
            if (totalTimeInterval) clearInterval(totalTimeInterval)
            const interval = setInterval(() => {
                const started = new Date(raceStartedMillis)
                const now = new Date()

                const diff = now.getTime() - started.getTime()
                const minutes = Math.floor((diff / 1000) / 60)
                const seconds = Math.floor((diff / 1000) % 60)

                setTotalTimeMinutes(prev => prev != minutes ? minutes : prev)
                setTotalTimeSeconds(prev => prev != seconds ? seconds : prev)
            }, 1000)
            setTotalTimeInterval(interval)
        }

        if (lapStartedMillis) {
            if (lapTimeInterval) clearInterval(lapTimeInterval)
            const interval = setInterval(() => {
                const started = new Date(lapStartedMillis)
                const now = new Date()

                const diff = now.getTime() - started.getTime()
                const minutes = Math.floor((diff / 1000) / 60)
                const seconds = Math.floor((diff / 1000) % 60)

                setLapTimeMinutes(prev => prev != minutes && minutes ? minutes : prev)
                setLapTimeSeconds(prev => prev != seconds && seconds ? seconds : prev)
            }, 1000)
            setLapTimeInterval(interval)
        }


        return () => {
            if (totalTimeInterval) clearInterval(totalTimeInterval)
            if (lapTimeInterval) clearInterval(lapTimeInterval)
        }
    }, [raceStartedMillis, lapStartedMillis, isRacing])

    const shouldShowConsumables = () => {
        return type == "race"
    }

    const getTotalLaps = (laps: number) => {
        if (type === "training" && laps === -999) {
            return "∞"
        }

        return laps.toString()
    }

    return (
        <div className="game-hud">
            <ShortTermAchievement
                title={shortTermAchivementTitle}
                x={shortTermAchivementX}
                animationKey={animationKey}
            />
            <div className='race-hud-container'>

                <div className='left-block'>
                    {
                        totalLaps !== 1 ? (
                            <div className='progress-container'>
                                <div className='progress-number'>
                                    <p>{lap}/{getTotalLaps(totalLaps)}</p>
                                </div>
                                <div className='progress-title'>
                                    <p>{t.laps}</p>
                                </div>
                            </div>
                        ) : (
                            <div className='progress-container'>
                                <div className='progress-number'>
                                    <p>{progress}%</p>
                                </div>
                                <div className='progress-title'>
                                    <p>{t.progress}</p>
                                </div>
                            </div>
                        )
                    }


                    <div className="race-time-container">

                        <div className='last-lap-time'>
                            <p>{t.last_lap}</p>
                            <p>
                                {getTimeFormated(null, lapTimeMinutes, lapTimeSeconds)}
                            </p>
                        </div>

                        <div className='best-lap-time'>
                            <p>{t.best_lap}</p>
                            <p>
                                {getTimeFormated(null, bestLapTimeMinutes, bestLapTimeSeconds)}
                            </p>
                        </div>

                        <div className='total-time'>
                            <p>{t.total_time}</p>
                            <p>
                                {getTimeFormated(null, totalTimeMinutes, totalTimeSeconds)}
                            </p>
                        </div>
                    </div>

                </div>

                <div className='right-block'>

                    <div className='position-container'>
                        <div className='position-title'>
                            <p>{t.position}</p>
                        </div>
                        <div className='position-number'>
                            <p>{position}/{totalPlayer}</p>
                        </div>
                    </div>

                </div>

            </div>

            <GameHudHints type={type} />

            {shouldShowConsumables() && <ConsumablesShowMenu fuel={fuel} nitro={nitro} />}
            {isDisqualified && <Disqualification
                place={position}
                text={t.disqualification_title}
                title={t.disqualification_desc_fuel}
            />}

            <Toaster
                position="bottom-center"
                reverseOrder={false}
            />
        </div>
    )
}